import type { Feature } from '@ardoq/api-types';
import {
  featureOperations,
  Features,
  type FeatureContext,
} from '@ardoq/features';
import { TOUR_IDS, type TourId } from './types';

/** Scoped to one surface the viewer is already on: nothing to offer, nowhere to send them. */
const SURFACE_SCOPED_TOUR_IDS = [
  'presentation-navigator',
  'improved-datasets-panel',
] as const;

export type CataloguedTourId = Exclude<
  TourId,
  (typeof SURFACE_SCOPED_TOUR_IDS)[number]
>;

const isCataloguedTourId = (tourId: TourId): tourId is CataloguedTourId =>
  !(SURFACE_SCOPED_TOUR_IDS as readonly string[]).includes(tourId);

export const CATALOGUED_TOUR_IDS: CataloguedTourId[] =
  TOUR_IDS.filter(isCataloguedTourId);

/** An app that renders the surfaces a tour's steps anchor to. */
export type TourHostApp = 'ardoq-front' | 'ardoq-surveys';

export type TourCatalogueData = {
  name: string;
  description: string;
  whenToUse: string;
  /** Every feature the tour needs; empty when it needs none beyond the domain gate. */
  requiredFeatures: Feature[];
  /**
   * The tour walks the user through editing data, so the controls it points at
   * are absent without write access and it is not offered to a reader.
   */
  requiresWriteAccess: boolean;
  requiresOrgAdmin: boolean;
  /**
   * The tour walks a specific piece of the organization's data — a survey to
   * answer, a populated component type to open — not merely a feature that
   * renders. Without it the steps have nothing to anchor to and nowhere to
   * navigate, so a launcher checks for it before offering the tour.
   */
  requiresTourData: boolean;
  /**
   * The apps whose surfaces this tour's steps live on. A tour that spans apps
   * lists all of them: its launch URL is valid in each, and each registers the
   * whole step list so the card's step counter agrees on both sides.
   */
  hostApps: TourHostApp[];
};

/** What a launcher knows about the user and about itself, as the catalogue's gates need it. */
export type TourGateContext = {
  featureContext: FeatureContext;
  hasWriteAccess: boolean;
  hostApp: TourHostApp;
  /**
   * The data-gated tours whose data the launcher found, for the tours that
   * declare `requiresTourData`. Every other tour ignores it.
   */
  toursWithSatisfiedDataGates: readonly TourId[];
};

/** One tour as advertised to the agent: its catalogue data, minus the gates that decided it belongs there. */
export type TourCatalogueEntry = Omit<
  TourCatalogueData,
  | 'requiredFeatures'
  | 'requiresWriteAccess'
  | 'requiresOrgAdmin'
  | 'requiresTourData'
  | 'hostApps'
> & {
  id: TourId;
};

/**
 * Static, LLM-facing descriptions of each tour. Kept separate from the cold
 * `tourRegistry` observables so the catalogue can be read without subscribing
 * to a tour's data source. English constants, not i18n — this text is shipped
 * to the assistant, not rendered to the user.
 *
 * `requiredFeatures`, `requiresWriteAccess`, `requiresOrgAdmin`,
 * `requiresTourData` and `hostApps` mirror the gating on the tour's launcher: a
 * tour the user cannot open from the UI is not offered by the agent either, and
 * a URL cannot hand out a tour this app does not host. The agent itself is
 * gated by `PRODUCT_TOUR_AGENT`.
 */
const tourCatalogue: Record<CataloguedTourId, TourCatalogueData> = {
  'component-overview': {
    name: 'Component Overview Page',
    description:
      'A guided walkthrough of a single component: field data, references, viewpoints, styles, and the survey that updates several fields at once.',
    whenToUse:
      'The user asks how to view or edit a single component, what the component overview page shows, or how to add references, fields, styles or a description to a component.',
    requiredFeatures: [
      Features.COMPONENT_OVERVIEW_2025,
      Features.CANVAS_PROTOTYPE,
    ],
    requiresWriteAccess: true,
    requiresOrgAdmin: false,
    requiresTourData: true,
    hostApps: ['ardoq-front'],
  },
  'homepage-cards-manage-data': {
    name: 'Manage homepage card data',
    description:
      'A guided walkthrough of curating the data behind a homepage card: selecting the card in Home settings, choosing its workspaces, and refining the component list with columns and filters before saving.',
    whenToUse:
      'The user asks how to edit, curate, or manage the data or component list behind a homepage card.',
    requiredFeatures: [Features.HOME_PAGE_SETTINGS],
    requiresWriteAccess: false,
    requiresOrgAdmin: true,
    requiresTourData: false,
    hostApps: ['ardoq-front'],
  },
  'discover-experience': {
    name: 'A New Discover Experience',
    description:
      'A guided walkthrough of the new Discover experience: the sidebar, search and Quick Links on the home page, the Data Inventory spreadsheet, a component overview page and its Viewpoints, opening a viewpoint into a view, and the Dashboards, Reports and Presentations shared with you.',
    whenToUse:
      'The user asks what has changed in Discover, how to get around the new Discover experience, or wants an overview of the whole product rather than one surface.',
    requiredFeatures: [
      Features.COMPONENT_OVERVIEW_2025,
      Features.DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION,
    ],
    requiresWriteAccess: false,
    requiresOrgAdmin: false,
    requiresTourData: false,
    hostApps: ['ardoq-front'],
  },
  'data-inventory': {
    name: 'Data Inventory quick start',
    description:
      'A guided walkthrough of Data Inventory: opening it from the sidebar, picking a component type or workspace, choosing columns, opening a component overview without leaving the grid, sorting and filtering, reviewing and clearing active filters in the filter bar, editing cells in place, showing the full hierarchy, creating components, saving as an Excel file or report, and visualizing components.',
    whenToUse:
      'The user asks what Data Inventory is or how to use it — how to find components, change which columns the table shows, filter or sort the grid, edit component values in bulk, view components as a hierarchy, create components, export or save as a report, or visualize components.',
    requiredFeatures: [Features.COMPONENT_OVERVIEW_2025],
    requiresWriteAccess: false,
    requiresOrgAdmin: false,
    requiresTourData: true,
    hostApps: ['ardoq-front'],
  },
  'data-inventory-whats-new-2026-10': {
    name: 'Work faster in Data Inventory',
    description:
      'A short walkthrough of recent Data Inventory updates: active filters in the bar above the table, copying and pasting references between cells, field groups in the column picker, the Expand all button in the hierarchy view, and opening the full component page in a new tab from the component overview drawer.',
    whenToUse:
      'The user asks what is new or has changed in Data Inventory, where the active filters went, whether references can be copied and pasted, how to find grouped fields in the column picker, how to expand every level of a hierarchy, or how to get a direct link to a component from Data Inventory.',
    requiredFeatures: [Features.COMPONENT_OVERVIEW_2025],
    requiresWriteAccess: false,
    requiresOrgAdmin: false,
    requiresTourData: true,
    hostApps: ['ardoq-front'],
  },
  'answer-surveys': {
    name: 'Open and answer surveys',
    description:
      'A guided walkthrough of answering a survey, from Ardoq into the surveys app: opening Surveys from the sidebar, searching the overview for the survey to answer, opening it, choosing existing entries over a new entry, picking the item to update, filling in the questions, selecting an owner where the survey asks for one, and submitting.',
    whenToUse:
      'The user asks how to answer, fill in or submit a survey, where to find the surveys they can answer, or how survey answers reach Ardoq.',
    requiredFeatures: [],
    requiresWriteAccess: false,
    requiresOrgAdmin: false,
    requiresTourData: true,
    hostApps: ['ardoq-front', 'ardoq-surveys'],
  },
};

const isTourAvailable = (
  id: CataloguedTourId,
  {
    featureContext,
    hasWriteAccess,
    hostApp,
    toursWithSatisfiedDataGates,
  }: TourGateContext
): boolean => {
  const {
    requiredFeatures,
    requiresWriteAccess,
    requiresOrgAdmin,
    requiresTourData,
    hostApps,
  } = tourCatalogue[id];
  return (
    hostApps.includes(hostApp) &&
    (hasWriteAccess || !requiresWriteAccess) &&
    (featureContext.isOrgAdmin || !requiresOrgAdmin) &&
    (toursWithSatisfiedDataGates.includes(id) || !requiresTourData) &&
    requiredFeatures.every(feature =>
      featureOperations.hasFeature(featureContext, feature)
    )
  );
};

const toCatalogueEntry = (id: CataloguedTourId): TourCatalogueEntry => {
  const { name, description, whenToUse } = tourCatalogue[id];
  return { id, name, description, whenToUse };
};

export const isTourHostedBy = (tourId: TourId, hostApp: TourHostApp): boolean =>
  isCataloguedTourId(tourId) &&
  tourCatalogue[tourId].hostApps.includes(hostApp);

export const isTourStartable = (
  tourId: TourId,
  gate: TourGateContext
): boolean => isCataloguedTourId(tourId) && isTourAvailable(tourId, gate);

export const getStartableTours = (
  gate: TourGateContext
): TourCatalogueEntry[] =>
  CATALOGUED_TOUR_IDS.filter(id => isTourAvailable(id, gate)).map(
    toCatalogueEntry
  );
